import { Alert, Box, Paper, Stack } from '@mui/material'
import type { GridColDef, GridRowId } from '@mui/x-data-grid'
import axios from 'axios'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import { useAlertContext } from '@/contexts/AlertContext'
import { ControllerConfigurationTree } from '@/features/controller/configuration/components/tree/ControllerConfigurationTree'
import { ControllerHeader } from '@/features/controller/configuration/components/tree/ControllerHeader'
import { ControllerTooltip } from '@/features/controller/configuration/components/tree/ControllerTooltip'
import { NodeErrors } from '@/features/controller/configuration/components/tree/NodeErrors'
import { UnsavedEditButton } from '@/features/controller/configuration/components/tree/UnsavedEditButton'
import { useEditableControllerConfigurationModel } from '@/features/controller/configuration/hooks/useEditableControllerConfigurationModel'
import type { ControllerNode } from '@/features/controller/configuration/models/controllerConfigTree'
import type { ControllerConfigurationModel } from '@/features/controller/configuration/models/controllerConfiguration'
import { isTillquistEnabled } from '@/features/controller/configuration/models/raspiProperties/tillquist'
import type { ControllerDescription } from '@/features/controller/types'
import { getElectricianViewUrl } from '@/features/controller/utils/electricianViewLinks'
import { StyledCustomDataGrid } from '@/features/resource/components/steering/StyledCustomDataGrid'
import CancelConfirmIconButtons from '@/features/site/components/CancelConfirmIconButtons'
import type { Site } from '@/features/site/types/site'

interface SingleControllerConfigTreeProps {
  controller: ControllerDescription
  site: Site | null
}

function hasAnyPowerMetersConfigured(configuration: ControllerConfigurationModel) {
  if ((configuration.appConfig?.powerMeters?.length ?? 0) > 0) {
    return true
  }
  if (configuration.raspiPropertiesConfig) {
    if (isTillquistEnabled(configuration.raspiPropertiesConfig.tillquistConfig)) {
      return true
    }
    for (const conn of configuration.raspiPropertiesConfig.legacyModbusConnections) {
      if (conn.meters.length > 0) {
        return true
      }
    }
  }
  return false
}

/*
 * This is a middle step towards having a table with the configuration of multiple controllers
 */
export function SingleControllerConfigTree({ controller, site }: Readonly<SingleControllerConfigTreeProps>) {
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(new Set([controller.id]))

  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()

  const {
    configurationModel,
    isLoading,
    latestConfigurationVersions,
    onConfigurationModelUpdated,
    onConfigurationSourceUpdated,
    discardUnsavedChanges,
    persistUnsavedChanges,
    unsavedChanges,
    sourceView,
  } = useEditableControllerConfigurationModel({ onNewPowerMeterAdded: alertNewPowerMeterAdded })

  async function onSave() {
    try {
      await persistUnsavedChanges()
      pushAlert({
        message: t('controllers.configuration.update_success'),
        severity: 'success',
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        pushAlert({
          message: t('controllers.configuration.update_error_conflict'),
          severity: 'error',
        })
      } else {
        pushAlert({
          message: t('controllers.configuration.update_error'),
          severity: 'error',
        })
      }
    }
  }

  function alertNewPowerMeterAdded() {
    pushAlert({
      action: site && (
        <CustomButton
          color="inheritText"
          href={getElectricianViewUrl(site, controller)}
          target="_blank"
          variant="contained"
        >
          {t('controllers.configuration.power_meters.new_power_meter_added_alert.open_electrician_ui')}
        </CustomButton>
      ),
      isPersistent: true,
      message: t('controllers.configuration.power_meters.new_power_meter_added_alert.message'),
      severity: 'info',
      title: t('controllers.configuration.power_meters.new_power_meter_added_alert.title'),
    })
  }

  const columns: GridColDef<ControllerNode>[] = [
    {
      field: 'equipmentNumber',
      flex: 4,
      renderCell: ({ row }) => (
        <ControllerHeader
          configurationModel={configurationModel}
          configurationSourceView={sourceView}
          controllerDescription={row.controllerDescription}
          onConfigurationSourceUpdated={onConfigurationSourceUpdated}
        />
      ),
    },
    {
      field: 'errors',
      minWidth: 58,
      maxWidth: 58,
      renderCell: ({ row }) => <NodeErrors errors={row.errors} />,
    },
    {
      align: 'right',
      flex: 3,
      field: 'changes',
      renderCell: () => (
        <Stack flexDirection="row" gap={0.5} padding={0.5}>
          {unsavedChanges.appJson && (
            <UnsavedEditButton
              controllerId={controller.id}
              previousConfigVersion={latestConfigurationVersions.appJson!}
              unsavedChange={unsavedChanges.appJson}
            />
          )}
          {unsavedChanges.raspiProperties && (
            <UnsavedEditButton
              controllerId={controller.id}
              previousConfigVersion={latestConfigurationVersions.raspiProperties!}
              unsavedChange={unsavedChanges.raspiProperties}
            />
          )}
        </Stack>
      ),
    },
    {
      align: 'right',
      field: 'saveActions',
      // We can't allow flex width as that could hide the important buttons
      minWidth: 116,
      renderCell: () =>
        hasChanges ? (
          <Box padding={0.5}>
            <CancelConfirmIconButtons disabled={false} onCancelClick={discardUnsavedChanges} onConfimClick={onSave} />
          </Box>
        ) : (
          <Stack padding={0.5}></Stack>
        ),
    },
  ]

  const hasChanges = Boolean(unsavedChanges.appJson) || Boolean(unsavedChanges.raspiProperties)
  const hasPowerMeters = hasAnyPowerMetersConfigured(configurationModel)

  return (
    <>
      <StyledCustomDataGrid
        columnHeaderHeight={0}
        columns={columns}
        detailPanelExpandedRowIds={expandedRowIds}
        disableColumnReorder={true}
        disableColumnSorting={true}
        getDetailPanelContent={() => (
          <ControllerConfigurationTree
            configuration={configurationModel}
            indent={true}
            onConfigurationUpdated={onConfigurationModelUpdated}
          />
        )}
        getDetailPanelHeight={() => 'auto'}
        getRowId={(row: ControllerNode) => row.controllerDescription.id}
        hideFooter={true}
        includeWrapper={true}
        isLoading={isLoading}
        rows={[{ controllerDescription: controller, errors: configurationModel.errors }]}
        style={hasChanges ? { border: '2px dashed lightblue' } : { border: '2px solid white' }}
        title={t('controllers.configuration.tree.title')}
        titleExtraInfo={<ControllerTooltip />}
        onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
      />
      {!hasPowerMeters && (
        <Paper>
          <Alert severity="info" variant="outlined">
            {t('controllers.configuration.power_meters.no_power_meters')}
          </Alert>
        </Paper>
      )}
    </>
  )
}
